import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CustomerLayout from "../CustomerLayout";
import { API } from "../../../config";

const DEFAULT_OFFERS = [
  {
    code: "EARLYACCESS",
    label: "Early Access",
    title: "Private Beta Access",
    description:
      "Join HaggleFreight during the private beta and help test real transportation workflows before public launch.",
    benefit: "Priority onboarding",
    audience: "Early customers",
  },
  {
    code: "LAUNCHCREDIT",
    label: "Launch",
    title: "Launch Credit",
    description:
      "Eligible early customers may receive launch credits for posting real loads during the testing period.",
    benefit: "Limited launch credit",
    audience: "Beta users",
  },
  {
    code: "BUSINESSTRIAL",
    label: "Business",
    title: "Business Trial",
    description:
      "Businesses can test load posting, tracking, communication, and negotiation tools before public rollout.",
    benefit: "Business trial access",
    audience: "Business customers",
  },
];

export default function PromotionsAndDiscounts() {
  const [offers, setOffers] = useState(DEFAULT_OFFERS);
  const [selectedCode, setSelectedCode] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loadingCode, setLoadingCode] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await fetch(`${API}/api/customer/launch-offers`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) return;

        const data = await response.json();

        if (Array.isArray(data?.offers) && data.offers.length > 0) {
          setOffers(data.offers);
        }
      } catch (err) {
        console.warn("Using default launch offers:", err);
      }
    };

    fetchOffers();
  }, [token]);

  const applyCode = async (code) => {
    const finalCode = code || manualCode;

    if (!finalCode?.trim()) {
      setError("Please enter or select a valid code.");
      setMessage("");
      return;
    }

    try {
      setLoadingCode(finalCode);
      setError("");
      setMessage("");

      const response = await fetch(`${API}/api/customer/apply-launch-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: finalCode.trim().toUpperCase() }),
      });

      if (!response.ok) {
        throw new Error("Code could not be applied.");
      }

      localStorage.setItem("customerLaunchCode", finalCode.trim().toUpperCase());
      setSelectedCode(finalCode.trim().toUpperCase());
      setMessage(`Code ${finalCode.trim().toUpperCase()} was applied successfully.`);
    } catch (err) {
      localStorage.setItem("customerLaunchCode", finalCode.trim().toUpperCase());
      setSelectedCode(finalCode.trim().toUpperCase());
      setMessage(`Code ${finalCode.trim().toUpperCase()} was saved for beta access.`);
    } finally {
      setLoadingCode("");
    }
  };

  return (
    <CustomerLayout>
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            mb: 1,
          }}
        >
          Early Access
        </Typography>

        <Typography
          color="text.secondary"
          sx={{
            maxWidth: 760,
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          View available beta access benefits, launch credits, and business trial options for early HaggleFreight users.
        </Typography>

        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            mb: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
            Beta Access Code
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Enter a beta access code provided by HaggleFreight.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              fullWidth
              label="Access Code"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
            />

            <Button
              variant="contained"
              onClick={() => applyCode(manualCode)}
              sx={{
                minHeight: 56,
                px: 4,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 800,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Apply
            </Button>
          </Box>
        </Paper>

        <Grid container spacing={2}>
          {offers.map((offer) => (
            <Grid item xs={12} md={4} key={offer.code}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Chip
                  label={offer.label}
                  size="small"
                  sx={{
                    alignSelf: "flex-start",
                    mb: 2,
                    fontWeight: 700,
                  }}
                />

                <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                  {offer.title}
                </Typography>

                <Typography color="text.secondary" sx={{ mb: 2, lineHeight: 1.55 }}>
                  {offer.description}
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Benefit
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {offer.benefit}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  Availability
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {offer.audience}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 800, mb: 2 }}>
                  Code: {offer.code}
                </Typography>

                <Box sx={{ mt: "auto" }}>
                  <Button
                    fullWidth
                    variant={selectedCode === offer.code ? "outlined" : "contained"}
                    disabled={loadingCode === offer.code}
                    onClick={() => applyCode(offer.code)}
                    sx={{
                      minHeight: 44,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 800,
                    }}
                  >
                    {selectedCode === offer.code ? "Applied" : "Apply"}
                  </Button>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </CustomerLayout>
  );
}